/**
 * Per-customer detail page.
 *
 * Loads ``GET /admin/customers/:id`` and renders six sections:
 *   1. Header — name, plan, status, MRR, employees
 *   2. Internal notes (editable textarea + Save)
 *   3. Users table
 *   4. Recent commands (last 20)
 *   5. Credit transactions (last 20)
 *   6. Subscription history (last 20 BillingEvent rows)
 *
 * The notes textarea is debounced — every keystroke would spam the
 * save endpoint. We save on blur and on an explicit ``Save`` button
 * click. A success toast tells the founder the row persisted.
 */
import { FormEvent, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Save } from "lucide-react";

import {
  CustomerDetail,
  getCustomer,
  saveCustomerNotes,
} from "@/api/admin";
import { cn, fmtDate, fmtNum, fmtRelative, fmtUsd } from "@/lib/utils";

export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<CustomerDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [notesStatus, setNotesStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");

  useEffect(() => {
    let cancelled = false;
    if (!id) return;
    getCustomer(id)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setNotes(d.internal_notes || "");
      })
      .catch((e) =>
        !cancelled && setError(e?.message || "Failed to load customer"),
      );
    return () => {
      cancelled = true;
    };
  }, [id]);

  const onSubmitNotes = async (e: FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setNotesStatus("saving");
    try {
      await saveCustomerNotes(id, notes);
      setNotesStatus("saved");
      window.setTimeout(() => setNotesStatus("idle"), 2_000);
    } catch {
      setNotesStatus("error");
    }
  };

  if (error) {
    return (
      <div className="p-8 text-sm text-danger">
        <p>Failed to load customer: {error}</p>
        <Link to="/customers" className="mt-3 inline-flex text-accent">
          ← Back to customers
        </Link>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="p-8 flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading…
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <Link
        to="/customers"
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-accent"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Customers
      </Link>

      {/* ── Header ─────────────────────────────────────────── */}
      <header className="bg-white rounded-lg border border-slate-200 p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              {data.name}
            </h1>
            <p className="text-xs text-slate-500 mt-1 font-mono">
              {data.slug} · {data.id}
            </p>
          </div>
          <div className="flex flex-wrap gap-4 text-right">
            <Metric label="MRR" value={fmtUsd(data.monthly_revenue_usd)} />
            <Metric label="Employees" value={fmtNum(data.employees)} />
            <Metric label="Plan" value={data.plan || "—"} />
            <Metric label="Status" value={data.status} />
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <KV label="Signup source" value={data.signup_source || "—"} />
          <KV label="Domain" value={data.domain || "—"} />
          <KV label="Created" value={fmtDate(data.created_at)} />
          <KV label="Trial ends" value={fmtDate(data.trial_ends_at)} />
        </div>
      </header>

      {/* ── Internal notes ─────────────────────────────────── */}
      <section className="bg-white rounded-lg border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-slate-900">
            Internal notes
          </h2>
          {notesStatus === "saved" && (
            <span className="text-xs text-success">Saved</span>
          )}
          {notesStatus === "error" && (
            <span className="text-xs text-danger">Save failed — retry</span>
          )}
        </div>
        <form onSubmit={onSubmitNotes}>
          <textarea
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              if (notesStatus !== "idle") setNotesStatus("idle");
            }}
            placeholder="CSM notes — calls, renewal flags, anything to remember about this account."
            rows={5}
            className="w-full px-3 py-2 text-sm rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent resize-y"
            maxLength={20_000}
          />
          <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
            <span>{notes.length.toLocaleString()} / 20,000 chars</span>
            <button
              type="submit"
              disabled={notesStatus === "saving"}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-white",
                notesStatus === "saving"
                  ? "bg-slate-300 cursor-not-allowed"
                  : "bg-accent hover:bg-accent-600",
              )}
            >
              {notesStatus === "saving" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              Save
            </button>
          </div>
        </form>
      </section>

      {/* ── Users ──────────────────────────────────────────── */}
      <Section title={`Users (${data.users.length})`}>
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Email</th>
              <th className="text-left px-4 py-2 font-semibold">Role</th>
              <th className="text-left px-4 py-2 font-semibold">Last login</th>
              <th className="text-left px-4 py-2 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.users.map((u) => (
              <tr key={u.id} className="hover:bg-slate-50">
                <td className="px-4 py-2 font-medium text-slate-900">
                  {u.email}
                </td>
                <td className="px-4 py-2 text-slate-600 text-xs uppercase tracking-wider">
                  {u.role}
                </td>
                <td className="px-4 py-2 text-slate-600">
                  {fmtRelative(u.last_login)}
                </td>
                <td className="px-4 py-2">
                  <span
                    className={cn(
                      "text-xs",
                      u.is_active ? "text-success" : "text-slate-400",
                    )}
                  >
                    {u.is_active ? "active" : "disabled"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* ── Recent commands ─────────────────────────────── */}
      <Section title={`Recent AI commands (${data.recent_commands.length})`}>
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">When</th>
              <th className="text-left px-4 py-2 font-semibold">Intent</th>
              <th className="text-left px-4 py-2 font-semibold">Status</th>
              <th className="text-left px-4 py-2 font-semibold">Command</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.recent_commands.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-slate-500">
                  No commands yet.
                </td>
              </tr>
            )}
            {data.recent_commands.map((c) => (
              <tr key={c.id}>
                <td className="px-4 py-2 text-slate-600 whitespace-nowrap">
                  {fmtRelative(c.created_at)}
                </td>
                <td className="px-4 py-2 text-slate-600 font-mono text-xs">
                  {c.intent_type}
                </td>
                <td className="px-4 py-2 text-slate-600 text-xs uppercase">
                  {c.status}
                </td>
                <td className="px-4 py-2 text-slate-700 truncate max-w-md">
                  “{c.command_text}”
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* ── Credit transactions ─────────────────────────── */}
      <Section title="Credit transactions">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">When</th>
              <th className="text-left px-4 py-2 font-semibold">Type</th>
              <th className="text-right px-4 py-2 font-semibold">Amount</th>
              <th className="text-right px-4 py-2 font-semibold">Balance after</th>
              <th className="text-left px-4 py-2 font-semibold">Description</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.recent_credit_transactions.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-slate-500">
                  No credit activity yet.
                </td>
              </tr>
            )}
            {data.recent_credit_transactions.map((t) => (
              <tr key={t.id}>
                <td className="px-4 py-2 text-slate-600 whitespace-nowrap">
                  {fmtRelative(t.created_at)}
                </td>
                <td className="px-4 py-2 text-slate-600 text-xs uppercase">
                  {t.type}
                </td>
                <td
                  className={cn(
                    "px-4 py-2 text-right tabular-nums",
                    t.amount < 0 ? "text-danger" : "text-success",
                  )}
                >
                  {t.amount > 0 ? "+" : ""}
                  {fmtNum(t.amount)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-slate-600">
                  {fmtNum(t.balance_after)}
                </td>
                <td className="px-4 py-2 text-slate-600">
                  {t.description || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* ── Subscription history ───────────────────────── */}
      <Section title="Subscription history">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">When</th>
              <th className="text-left px-4 py-2 font-semibold">Event</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.subscription_history.length === 0 && (
              <tr>
                <td colSpan={2} className="px-4 py-6 text-center text-slate-500">
                  No subscription events yet.
                </td>
              </tr>
            )}
            {data.subscription_history.map((b) => (
              <tr key={b.id}>
                <td className="px-4 py-2 text-slate-600 whitespace-nowrap">
                  {fmtRelative(b.created_at)}
                </td>
                <td className="px-4 py-2 text-slate-600 font-mono text-xs">
                  {b.event_type}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-right">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="text-lg font-semibold tabular-nums text-slate-900 capitalize">
        {value}
      </div>
    </div>
  );
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="text-sm text-slate-900">{value}</div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <header className="px-4 py-2.5 border-b border-slate-200">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      </header>
      <div className="overflow-x-auto">{children}</div>
    </section>
  );
}
